import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../FishFinderBuilder.css';
import FeedbackSection from './FeedbackSection';
import { getDealLinkById } from '../data/dealLinks';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import CategoryButton from './CategoryButton';
import { submitFeedback } from '../utils/api';

const STORAGE_KEY = 'fishFinderSelections';

// Helper function to safely parse localStorage data
const getSavedSelections = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

// Helper function to safely save to localStorage
const saveSelections = (selections) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const FishFinderBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  
  // Add state for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Initialize state with saved values
  const savedSelections = getSavedSelections();
  
  const [selectedScreen, setSelectedScreen] = useState(savedSelections?.selectedScreen || null);
  const [selectedLivescope, setSelectedLivescope] = useState(savedSelections?.selectedLivescope || null);
  const [selectedOpenWater, setSelectedOpenWater] = useState(savedSelections?.selectedOpenWater || null);
  const [selectedIceFisher, setSelectedIceFisher] = useState(savedSelections?.selectedIceFisher || null);
  const [battery, setBattery] = useState(savedSelections?.battery || null);
  const [shuttle, setShuttle] = useState(savedSelections?.shuttle || null);
  const [pole, setPole] = useState(savedSelections?.pole || null);

  // Save selections whenever they change
  useEffect(() => {
    const selections = {
      selectedScreen,
      selectedLivescope,
      selectedOpenWater,
      selectedIceFisher,
      battery,
      shuttle,
      pole
    };
    saveSelections(selections);
  }, [selectedScreen, selectedLivescope, selectedOpenWater, selectedIceFisher, battery, shuttle, pole]);

  // Custom state setters that also update localStorage
  const updateSelectedScreen = (newScreen) => {
    setSelectedScreen(newScreen);
    saveSelections({
      ...getSavedSelections(),
      selectedScreen: newScreen
    });
  };

  const updateSelectedLivescope = (newLivescope) => {
    setSelectedLivescope(newLivescope);
    saveSelections({
      ...getSavedSelections(),
      selectedLivescope: newLivescope
    });
  };

  const updateSelectedOpenWater = (newOpenWater) => {
    setSelectedOpenWater(newOpenWater);
    saveSelections({
      ...getSavedSelections(),
      selectedOpenWater: newOpenWater
    });
  };

  const updateSelectedIceFisher = (newIceFisher) => {
    setSelectedIceFisher(newIceFisher);
    saveSelections({
      ...getSavedSelections(),
      selectedIceFisher: newIceFisher
    });
  };

  const updateBattery = (newBattery) => {
    setBattery(newBattery);
    saveSelections({
      ...getSavedSelections(),
      battery: newBattery
    });
  };

  const updateShuttle = (newShuttle) => {
    setShuttle(newShuttle);
    saveSelections({
      ...getSavedSelections(),
      shuttle: newShuttle
    });
  };

  const updatePole = (newPole) => {
    setPole(newPole);
    saveSelections({
      ...getSavedSelections(),
      pole: newPole
    });
  };

  // Update state from location when returning from selection flow
  useEffect(() => {
    if (location.state) {
      const {
        selectedScreen: newScreen,
        selectedLivescope: newLivescope,
        selectedOpenWater: newOpenWater,
        selectedIceFisher: newIceFisher,
        battery: newBattery,
        shuttle: newShuttle,
        pole: newPole
      } = location.state;

      if (newScreen !== undefined) updateSelectedScreen(newScreen);
      if (newLivescope !== undefined) updateSelectedLivescope(newLivescope);
      if (newOpenWater !== undefined) updateSelectedOpenWater(newOpenWater);
      if (newIceFisher !== undefined) updateSelectedIceFisher(newIceFisher);
      if (newBattery !== undefined) updateBattery(newBattery);
      if (newShuttle !== undefined) updateShuttle(newShuttle);
      if (newPole !== undefined) updatePole(newPole);

      // Scroll to top of page when returning from selection pages
      window.scrollTo(0, 0);

      // Clear location state after processing
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Calculate total
  const calculateTotal = () => {
    return (
      (selectedScreen?.msrp || 0) +
      (selectedLivescope?.msrp || 0) +
      (selectedOpenWater?.msrp || 0) +
      (selectedIceFisher?.msrp || 0) +
      (battery?.msrp || 0) +
      (shuttle?.msrp || 0) +
      (pole?.msrp || 0)
    );
  };

  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleNavigateToSelection = (componentType, transducerType = null) => {
    // For screens, navigate to the new comparison page
    if (componentType === "screen") {
      navigate('/compare-screens', {
        state: {
          selectedScreen,
          selectedLivescope,
          selectedOpenWater,
          selectedIceFisher,
          battery,
          shuttle,
          pole
        }
      });
      return;
    }

    // For battery component, include the active transducer's information
    if (componentType === "battery") {
      // Determine the active transducer based on which one is selected
      const activeTransducer = selectedLivescope || selectedOpenWater || selectedIceFisher;
      
      navigate(`/select/${componentType}`, {
        state: {
          selectedScreen,
          transducer: activeTransducer,
          battery,
          shuttle,
          pole,
        }
      });
      return;
    }

    // For other components, use the existing selection flow
    navigate(`/select/${componentType}`, {
      state: {
        selectedScreen,
        selectedLivescope,
        selectedOpenWater,
        selectedIceFisher,
        battery,
        shuttle,
        pole,
        transducerType
      }
    });
  };

  // Helper function to get product link
  const getProductLink = (component, type) => {
    if (!component) return null;
    
    // Special case for LiveScope Plus LVS34
    if (component.name === "LiveScope Plus LVS34") {
      return "/simple-products/livescope-plus-lvs34";
    }
    
    // Use the simple product pages for all products if they have an ID
    if (component.id) {
      return `/simple-products/${component.id.toLowerCase().replace(/\s+/g, '-')}`;
    }
    
    // Fall back to the old logic if needed
    return component.productLink || getDealLinkById(type, component.id);
  };

  const categories = [
    {
      title: "Screen",
      selected: selectedScreen,
      componentType: "screen",
      description: "",
      price: selectedScreen?.msrp,
      productLink: getProductLink(selectedScreen, 'screen')
    },
    {
      title: "Live Imaging Transducers",
      selected: selectedLivescope,
      componentType: "transducer",
      transducerType: "liveImaging",
      description: "",
      price: selectedLivescope?.msrp || 0,
      productLink: selectedLivescope?.name === "LiveScope Plus LVS34" ? 
        "/simple-products/livescope-plus-lvs34" : 
        getProductLink(selectedLivescope, 'livescope')
    },
    {
      title: "Open Water Transducers",
      selected: selectedOpenWater,
      componentType: "transducer",
      transducerType: "openWater",
      description: "",
      price: selectedOpenWater?.msrp || 0,
      productLink: getProductLink(selectedOpenWater, 'openWater')
    },
    {
      title: "Ice Fishing Transducers",
      selected: selectedIceFisher,
      componentType: "transducer",
      transducerType: "iceFishing",
      description: "",
      price: selectedIceFisher?.msrp || 0,
      productLink: getProductLink(selectedIceFisher, 'iceFishing')
    },
    {
      title: "Battery",
      selected: battery,
      componentType: "battery",
      description: "",
      price: battery?.msrp,
      productLink: getProductLink(battery, 'battery')
    },
    {
      title: "Shuttle",
      selected: shuttle,
      componentType: "shuttle",
      description: "",
      price: shuttle?.msrp,
      productLink: getProductLink(shuttle, 'shuttle')
    },
    {
      title: "Pole Mount",
      selected: pole,
      componentType: "pole",
      description: "",
      price: pole?.msrp,
      productLink: getProductLink(pole, 'pole')
    }
  ];

  // Update clear all function to show confirmation first
  const handleClearAll = () => {
    setShowConfirmation(true);
  };

  // Function to actually clear selections after confirmation
  const confirmClear = () => {
    setSelectedScreen(null);
    setSelectedLivescope(null);
    setSelectedOpenWater(null);
    setSelectedIceFisher(null);
    setBattery(null);
    setShuttle(null);
    setPole(null);
    localStorage.removeItem(STORAGE_KEY);
    setShowConfirmation(false);
  };

  // Function to cancel clearing
  const cancelClear = () => {
    setShowConfirmation(false);
  };

  const handleRemoveItem = (category) => {
    switch (category) {
      case "Screen":
        updateSelectedScreen(null);
        break;
      case "Open Water Transducers":
        updateSelectedOpenWater(null);
        break;
      case "Live Imaging Transducers":
        updateSelectedLivescope(null);
        break;
      case "Ice Fishing Transducers":
        updateSelectedIceFisher(null);
        break;
      case "Battery":
        updateBattery(null);
        break;
      case "Shuttle":
        updateShuttle(null);
        break;
      case "Pole Mount":
        updatePole(null);
        break;
    }
  };

  return (
    <div className="fish-finder-builder-page" style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: '0',
      padding: '0 8px',
      background: theme.colors.background,
      minHeight: '100vh',
      position: 'relative'  // Add this for modal positioning
    }}>
      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: theme.colors.surface,
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: theme.colors.text,
              fontSize: '18px'
            }}>Clear All Selections?</h3>
            <p style={{
              margin: '0 0 24px 0',
              color: theme.colors.textSecondary,
              fontSize: '14px'
            }}>Are you sure you want to clear this build? This action cannot be undone.</p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={cancelClear}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  color: theme.isDarkMode ? '#fff' : '#333',
                  border: `1px solid ${theme.isDarkMode ? '#555' : '#ccc'}`,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmClear}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        margin: '5px 0 0 0',
        padding: '0 24px 0 0',
        display: 'none'  // Hide the original theme toggle container
      }}>
        <ThemeToggle isDarkMode={theme.isDarkMode} onToggle={toggleTheme} />
      </div>

      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        marginTop: '12px',
        marginBottom: '4px'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          color: theme.colors.text,
          margin: 0
        }}>Custom Fish Finder Builder</h1>
      </div>

      <div style={{ 
        display: 'flex',
        gap: '0px',
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        {[
          'Choose components & build your bundle',
          'Save with direct links to deals',
          'Ice & open water fishing setups'
        ].map((feature, index) => (
          <div key={index} style={{ 
            display: 'flex',
            alignItems: 'center',
            color: theme.colors.text,
            fontSize: '12px',
            padding: '4px 16px',
            background: 'transparent',
            borderRadius: '8px',
            opacity: 0.9
          }}>
            <span style={{ 
              color: '#4CAF50',
              marginRight: '8px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>✓</span>
            {feature}
          </div>
        ))}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <ThemeToggle isDarkMode={theme.isDarkMode} onToggle={toggleTheme} />
        <button
          onClick={handleClearAll}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: 'transparent',
            color: theme.isDarkMode ? '#fff' : '#333',
            border: `1px solid ${theme.isDarkMode ? '#555' : '#ccc'}`,
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Clear All Selections
        </button>
      </div>

      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {categories.map((category) => (
          <div key={category.title}>
            <h2 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: theme.colors.text,
              margin: 0,
              marginBottom: '4px'
            }}>{category.title}</h2>
            
            {category.selected ? (
              <div style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '12px',
                overflow: 'hidden',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '80px',
                  height: '60px',
                  background: '#fff',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  {category.selected.image && (
                    <img 
                      src={category.selected.image} 
                      alt={category.selected.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://dummyimage.com/80x60/cccccc/000000&text=${category.title}`;
                      }}
                    />
                  )}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.85em',
                    fontWeight: '500',
                    color: theme.colors.text,
                    marginBottom: '4px'
                  }}>{category.selected.name}</div>
                  <div style={{
                    fontSize: '0.85em',
                    color: theme.colors.textSecondary,
                    marginBottom: '8px'
                  }}>MSRP: {formatPrice(category.selected.msrp)}</div>
                  
                  {category.productLink && (
                    <Link 
                      to={category.productLink}
                      style={{
                        padding: '6px 12px',
                        background: theme.colors.accent,
                        color: '#fff',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '0.75em',
                        fontWeight: '500',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background 0.2s ease',
                        marginBottom: '8px',
                        '&:hover': {
                          background: theme.colors.accentHover
                        }
                      }}
                    >
                      💰 Find Best Price
                    </Link>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <button 
                    onClick={() => handleNavigateToSelection(category.componentType, category.transducerType)}
                    style={{
                      padding: '8px',
                      background: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '6px',
                      color: theme.colors.text,
                      fontSize: '14px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: theme.colors.surfaceHover,
                        borderColor: theme.colors.borderHover
                      }
                    }}
                  >
                    ⇄
                  </button>
                  
                  <button 
                    onClick={() => handleRemoveItem(category.title)}
                    style={{
                      padding: '8px',
                      background: 'transparent',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '6px',
                      color: '#dc3545',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: '#fff5f5',
                        borderColor: '#ff8787'
                      }
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: '8px' }}>
                <CategoryButton 
                  onClick={() => handleNavigateToSelection(category.componentType, category.transducerType)} 
                  theme={theme}
                >
                  {category.title === "Screen" ? 'Select Screen' : 
                   category.title === "Open Water Transducers" ? 'Select Open Water Transducer' : 
                   category.title === "Live Imaging Transducers" ? 'Select Live Imaging Transducer' : 
                   category.title === "Ice Fishing Transducers" ? 'Select Ice Transducer' : 
                   category.title === "Battery" ? 'Select Battery' :
                   category.title === "Shuttle" ? 'Select Shuttle' :
                   category.title === "Pole Mount" ? 'Select Pole' :
                   'Select Components'}
                </CategoryButton>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Hot Deals Section */}
      <div style={{
        marginBottom: '32px',
        background: theme.isDarkMode ? theme.colors.surface : '#e8e8e8',
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: theme.colors.text,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ color: theme.colors.accent }}>🔥</span> Hot Deals on Pre-Built Bundles
          </h2>
          <span style={{
            fontSize: '14px',
            color: '#00a854',
            background: 'rgba(0, 168, 84, 0.1)',
            padding: '4px 10px',
            borderRadius: '6px',
            fontWeight: '500'
          }}>Save up to $500</span>
        </div>
        
        <div style={{
          padding: '16px 20px',
          overflowX: 'auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            {[
              {
                name: "Pro Angler Bundle",
                price: 2499.99,
                savings: 500,
                image: "/images/pro-bundle.jpg",
                feature: "9\" GPSMAP + LiveScope"
              },
              {
                name: "Weekend Warrior",
                price: 1499.99,
                savings: 300,
                image: "/images/weekend-bundle.jpg",
                feature: "7\" ECHOMAP + Sonar"
              },
              {
                name: "Ice Fishing Special",
                price: 999.99,
                savings: 200,
                image: "/images/ice-bundle.jpg",
                feature: "5\" STRIKER + Ice Kit"
              },
              {
                name: "Elite Package",
                price: 3499.99,
                savings: 700,
                image: "/images/elite-bundle.jpg",
                feature: "12\" GPSMAP + LiveScope"
              }
            ].map((bundle, index) => (
              <div key={index} style={{
                background: theme.colors.surface,
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.colors.cardShadow,
                  borderColor: theme.colors.borderHover
                }
              }}>
                <div style={{
                  padding: '16px',
                  borderBottom: `1px solid ${theme.colors.border}`
                }}>
                  <img 
                    src={bundle.image} 
                    alt={bundle.name}
                    style={{
                      width: '100%',
                      height: '160px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://dummyimage.com/250x160/cccccc/000000&text=${bundle.name}`;
                    }}
                  />
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: theme.colors.text,
                    marginBottom: '4px'
                  }}>{bundle.name}</div>
                  <div style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '12px'
                  }}>{bundle.feature}</div>
                  <div style={{
                    color: '#00a854',
                    fontSize: '14px',
                    fontWeight: '500',
                    background: 'rgba(0, 168, 84, 0.1)',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>Save ${bundle.savings}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="feedback-container">
        <FeedbackSection
          selectedScreen={selectedScreen}
          transducer={selectedLivescope || selectedOpenWater || selectedIceFisher}
          battery={battery}
          shuttle={shuttle}
          pole={pole}
          totalMSRP={calculateTotal()}
          showHeader={true}
        />
      </div>
    </div>
  );
};

export default FishFinderBuilder; 